import { z } from 'zod';
const baseDiagnosis = z.object({
    code: z.string(),
    description: z.string(),
    severity: z.string(),
});
export const otEvalValidationSchema = z.object({
    id: z.string(),
    data: z.object({
        General: z.boolean().nullish(),
        HistoryDiagnosis: z.object({
            diagnosis: z.object({
                diagnoses: z.array(baseDiagnosis).superRefine((input, ctx) => {
                    input.forEach((i, index) => {
                        if (!i.severity) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Severity required`,
                                path: [index, 'severity'],
                            });
                        }
                        if (!i.code) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Code required`,
                                path: [index, 'code'],
                            });
                        }
                    });
                }),
                currentPTDiagnosis: z.string().nullish(),
                eventLeadingToEpisode: z.string().nullish(),
                pmhx: z.string().nullish(),
                fallHistory: z.string().nullish(),
                patientConcerns: z.string().nullish(),
            }),
            livingSituation: z.object({
                livingSituation: z.string().nullish(),
                livingSituationOther: z.string().nullish(),
            }),
            medEquipment: z.object({
                dmePresent: z.string().nullish(),
            }),
            mentalStatus: z.object({
                alertness: z.string().nullish(),
                orientedToPerson: z.string().nullish(),
                orientedToPlace: z.string().nullish(),
                orientedToPurpose: z.string().nullish(),
                orientedToTime: z.string().nullish(),
                status: z.array(z.string()).optional(),
                aggravatingFactor: z.string().nullish(),
            }),
            followCommands: z.array(z.string()).optional(),
            assistanceFrom: z.object({
                current: z.string().nullish(),
                prior: z.string().nullish(),
            }),
            vitals: z.object({
                bp: z.object({
                    rest: z.object({
                        diastolic: z.string().optional(),
                        systolic: z.string().optional(),
                    }),
                    activity: z.object({
                        diastolic: z.string().optional(),
                        systolic: z.string().optional(),
                    }),
                }),
                sp: z.object({
                    rest: z.string().optional(),
                    activity: z.string().optional(),
                }),
                heartRate: z.object({
                    rest: z.string().optional(),
                    activity: z.string().optional(),
                }),
                respiration: z.object({
                    rest: z.string().optional(),
                    activity: z.string().optional(),
                }),
            }),
            height: z.string().optional(),
            weight: z.string().optional(),
            language: z.object({
                preferredLanguage: z.string().nullish(),
                preferredLanguageOther: z.string().nullish(),
            }),
        }),
        ClinicalImpairment: z.object({
            strength: z.object({
                strengthAffecting: z.array(z.string()).optional(),
                romAffecting: z.array(z.string()).optional(),
                posture: z.string().nullish(),
                handDominance: z.array(z.string()).optional(),
                cervicalLeftStrength: z.string().nullish(),
                cervicalRightStrength: z.string().nullish(),
                shoulderLeftStrength: z.string().nullish(),
                shoulderRightStrength: z.string().nullish(),
                elbowLeftStrength: z.string().nullish(),
                elbowRightStrength: z.string().nullish(),
                pronLeftStrength: z.string().nullish(),
                pronRightStrength: z.string().nullish(),
                wristLeftStrength: z.string().nullish(),
                wristRightStrength: z.string().nullish(),
                handLeftStrength: z.string().nullish(),
                handRightStrength: z.string().nullish(),
                impression: z.string().nullish(),
            }),
            integumentary: z.object({
                edemaLUE: z.string().nullish(),
                edemaRUE: z.string().nullish(),
                edemaLLE: z.string().nullish(),
                edemaRLE: z.string().nullish(),
                leftPedal: z.string().nullish(),
                rightPedal: z.string().nullish(),
                girthMeasurement: z.string().nullish(),
                skinTemperature: z.string().nullish(),
                skinCondition: z.array(z.string()).optional(),
            }),
            balance: z.object({
                staticSeating: z.string().nullish(),
                staticStanding: z.string().nullish(),
                balanceSeating: z.string().nullish(),
                balanceStanding: z.string().nullish(),
                dynamicSeated: z.string().nullish(),
                asstToUnsupported: z.boolean().optional(),
                singleLeg: z.string().nullish(),
                rightingReactions: z.string().nullish(),
                equilibrium: z.string().nullish(),
                protectiveReactions: z.string().nullish(),
                standardizedTests: z.string().nullish(),
                tinettiTest: z.string().nullish(),
                timeUpAndGoTest: z.string().nullish(),
            }),
            functionalReachTest: z.object({
                position: z.string().nullish(),
                reach: z.string().nullish(),
                comments: z.string().nullish(),
            }),
            modifiedFunctionalReachTest: z.object({
                position: z.string().nullish(),
                reach: z.string().nullish(),
                comments: z.string().nullish(),
                chairRase: z.string().nullish(),
            }),
            clinicalImpression: z.string().nullish(),
            neurological: z.object({
                sensation: z.array(z.string()).optional(),
                affecting: z.array(z.string()).optional(),
                affectingOther: z.string().nullish(),
            }),
            motorControl: z.object({
                motorControl: z.string().nullish(),
                severity: z.string().nullish(),
                proprioception: z.string().nullish(),
                proprioceptionFindings: z.string().nullish(),
                kinesthesia: z.string().nullish(),
                kinesthesiaFindings: z.string().nullish(),
            }),
            tone: z.object({
                tone: z.string().nullish(),
                hearing: z.string().nullish(),
                hearingComments: z.string().nullish(),
                vision: z.string().nullish(),
                visionComments: z.string().nullish(),
            }),
            painAssessment: z.object({
                best: z.string().nullish(),
                during: z.string().nullish(),
                atRest: z.string().nullish(),
                worst: z.string().nullish(),
                location: z.string().nullish(),
                typeOfPain: z.string().nullish(),
                acute: z.string().nullish(),
                painExacerbated: z.string().nullish(),
                impactingADL: z.string().nullish(),
                painManaged: z.string().nullish(),
                impression: z.string().nullish(),
            }),
            activities: z.object({
                openDoor: z.string().nullish(),
                stepUpDown: z.string().nullish(),
                liftObjects: z.string().nullish(),
                carryObjects: z.string().nullish(),
                stairs: z.string().nullish(),
                car: z.string().nullish(),
                impression: z.string().nullish(),
                fallRisk: z.string().nullish(),
            }),
        }),
        FunctionalStatus: z.object({
            adl: z.object({
                grooming: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                toileting: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                bathing: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                dressingUE: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                dressingLE: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                socks: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
            }),
            transfers: z.object({
                bed: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                sitStand: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                toilet: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                chair: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                floor: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                shower: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                car: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
            }),
            iadl: z.object({
                usingPhones: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                shopping: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                foodPrep: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                houseKeeping: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                laundry: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                transport: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                takingMed: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
                finance: z
                    .object({
                    goal: z.string().optional(),
                    plof: z.string().optional(),
                    clof: z.string().optional(),
                })
                    .optional(),
            }),
            homeSafetyChecklist: z.object({
                motionDetection: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                overheadLighting: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                securitySystem: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                ramp: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                handrails: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                walkwayFromParkingToHome: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                frontDoor: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                mats: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                doorknob: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                stairlift: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                nonslipStairTreads: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                handrailing: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                clearWalkway: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                noLowLevelTables: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                phoneChargingStation: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                bedEasyToGetOn: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                roomClutterFree: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                chargingStationNearBed: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                mountGrabBars: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                walkInTubShower: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                addNonSlipMatShowerFloor: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                replaceLowToilets: z.object({
                    check: z.string().nullish(),
                    note: z.string().nullish(),
                }),
                impression: z.string().nullish(),
            }),
            cardiopulmonary: z.object({
                orthostatic: z.boolean().nullish(),
                metLevels: z.string().nullish(),
                rpe: z.string().nullish(),
                dyspnea: z.boolean().nullish(),
                dyspneaDescribe: z.string().nullish(),
            }),
            conditionKnowledge: z.object({
                knowledge: z.string().nullish(),
                rehabPotential: z.string().nullish(),
            }),
            homeboundReason: z.object({
                reason: z.array(z.string()).optional(),
                reasonOther: z.string().nullish(),
                precautions: z.string().nullish(),
            }),
            dcPlan: z.array(z.string()).optional(),
            potentialBarriers: z.string().nullish(),
            initialTreatment: z.object({
                o: z.string().nullish(),
                a: z.string().nullish(),
            }),
            communicationWith: z.object({
                communicationWith: z.array(z.string()).optional(),
                communicationWithReason: z.string().nullish(),
            }),
            otherDisciplinesProvidingCare: z.array(z.string()).optional(),
            equipmentNeeds: z.string().nullish(),
            reasonForOT: z.string().nullish(),
        }),
        PlanOfCare: z.object({
            treatmentPlan: z.object({
                ptEval: z.string().optional(),
                patientAgrees: z.boolean().optional(),
                patientAgreesTo: z.string().nullish(),
            }),
            rehab: z.object({
                left: z.array(z.string()).optional(),
                right: z.array(z.string()).optional(),
            }),
            therapyExercise: z.object({
                wnl: z.boolean().optional(),
                exercises: z.array(z.string()).optional(),
                promLeft: z.array(z.string()).optional(),
                promRight: z.array(z.string()).optional(),
                aromLeft: z.array(z.string()).optional(),
                aromRight: z.array(z.string()).optional(),
                isomLeft: z.array(z.string()).optional(),
                isomRight: z.array(z.string()).optional(),
                preLeft: z.array(z.string()).optional(),
                preRight: z.array(z.string()).optional(),
                cervical: z.array(z.string()).optional(),
            }),
            cardiopulmonary: z.object({
                wnl: z.boolean().optional(),
                tolerance: z.string().nullish(),
                activity: z.array(z.string()).optional(),
                monitor: z.array(z.string()).optional(),
                orders: z.array(z.string()).optional(),
                ordersFollow: z.string().nullish(),
                breathingExercises: z.array(z.string()).optional(),
                energyTechnique: z.array(z.string()).optional(),
            }),
            dyspnea: z.boolean().nullish(),
            highestFunctionalLevel: z.string().nullish(),
            painManagement: z.object({
                wnl: z.boolean().optional(),
                pain: z.array(z.string()).optional(),
            }),
            functionalActivities: z.object({
                wnl: z.boolean().optional(),
                activities: z.array(z.string()).optional(),
            }),
            adliadl: z.object({
                wnl: z.boolean().optional(),
                safeTechnique: z.array(z.string()).optional(),
                compensatoryTechnique: z.array(z.string()).optional(),
                adaptiveTechnique: z.array(z.string()).optional(),
                activities: z.array(z.string()).optional(),
                activitiesOther: z.string().nullish(),
            }),
            bedMobility: z.object({
                wnl: z.boolean().optional(),
                mobility: z.array(z.string()).optional(),
            }),
            manualTherapy: z.object({
                wnl: z.boolean().optional(),
                therapy: z.array(z.string()).optional(),
            }),
            gait: z.object({
                wnl: z.boolean().optional(),
                training: z.array(z.string()).optional(),
                sequencing: z.string().nullish(),
                assistiveDevice: z.string().nullish(),
                cadence: z.string().nullish(),
                surfaces: z.string().nullish(),
                baseSupport: z.string().nullish(),
                wb: z.string().nullish(),
                wbIndicate: z.array(z.string()).optional(),
            }),
            transfers: z.object({
                wnl: z.boolean().optional(),
                notes: z.string().nullish(),
                from: z.array(z.string()).optional(),
                fromProperTechnique: z.string().nullish(),
                fromOther: z.string().nullish(),
            }),
            balanceTransfer: z.object({
                wnl: z.boolean().optional(),
                transfer: z.array(z.string()).optional(),
            }),
            otcgEducation: z.object({
                wnl: z.boolean().optional(),
                otcgEducation: z.array(z.string()).optional(),
                assessment: z.string().nullish(),
            }),
            frequencyOfVisits: z.object({
                noOfVisits: z.string().optional(),
                recurring: z.string().optional(),
                otherDisciplineRecommend: z.array(z.string()).optional(),
                patientShowImprovement: z.string(),
            }),
        }),
    }),
    signedUserId: z.string().min(1, 'required'),
    signedUserDate: z.string().min(1, 'required'),
});
